/* eslint-disable import/no-unused-modules */
import hydraLogoBlack from 'assets/hydra/Hydra_Logo_Black.png'
import hydraLogoBlue from 'assets/hydra/Hydra_Logo_Blue.png'
import hydraLogoWhite from 'assets/hydra/Hydra_Logo_White.png'
import polygonCircleLogoUrl from 'assets/images/polygonCircle.png'
import ms from 'ms.macro'
import { darkTheme } from 'theme/colors'
import { getEnvironmentVariable } from 'utils/env'

import { SupportedChainId, SupportedL1ChainId } from './chains'
import { DEVNET_HYDRASWAP_LIST, MAINNET_HYDRASWAP_LIST, TESTNET_HYDRASWAP_LIST } from './lists'

export const AVERAGE_L1_BLOCK_TIME = ms`1s` // SAMVI Update: Update if not correct

export const IS_PROD = getEnvironmentVariable('REACT_APP_ENV') === 'prod'

export const MAINNET_WHYDRA_ADDRESS = getEnvironmentVariable('REACT_APP_MAINNET_WHYDRA_ADDRESS')
export const MAINNET_EXPLORER = getEnvironmentVariable('REACT_APP_MAINNET_EXPLORER')

export const TESTNET_WHYDRA_ADDRESS = getEnvironmentVariable('REACT_APP_TESTNET_WHYDRA_ADDRESS')
export const TESTNET_EXPLORER = getEnvironmentVariable('REACT_APP_TESTNET_EXPLORER')

export const DEVNET_WHYDRA_ADDRESS = getEnvironmentVariable('REACT_APP_DEVNET_WHYDRA_ADDRESS')
export const DEVNET_EXPLORER = getEnvironmentVariable('REACT_APP_DEVNET_EXPLORER')

export const HYDRACHAIN_DEX_URL = getEnvironmentVariable('REACT_APP_HYDRACHAIN_DEX_URL')
export const HYDRACHAIN_DEX_INFO_URL = getEnvironmentVariable('REACT_APP_HYDRACHAIN_DEX_INFO_URL')

export const HYDRACHAIN_DOCS_URL = getEnvironmentVariable('REACT_APP_HYDRACHAIN_DOCS_URL')
export const HYDRACHAIN_INFO_URL = getEnvironmentVariable('REACT_APP_HYDRACHAIN_INFO_URL')
export const HYDRACHAIN_WEB_URL = getEnvironmentVariable('REACT_APP_HYDRACHAIN_WEB_URL')
export const HYDRACHAIN_DEVELOPERS_URL = getEnvironmentVariable('REACT_APP_HYDRACHAIN_DEVELOPERS_URL')
export const HYDRACHAIN_CONTACT_URL = getEnvironmentVariable('REACT_APP_HYDRACHAIN_CONTACT_URL')
export const HYDRACHAIN_GITHUB_URL = getEnvironmentVariable('REACT_APP_HYDRACHAIN_GITHUB_URL')
export const HYDRACHAIN_X_URL = getEnvironmentVariable('REACT_APP_HYDRACHAIN_X_URL')
export const HYDRACHAIN_TELEGRAM_URL = getEnvironmentVariable('REACT_APP_HYDRACHAIN_TELEGRAM_URL')
export const HYDRACHAIN_PRIVACY_POLICY_URL = getEnvironmentVariable('REACT_APP_HYDRACHAIN_PRIVACY_POLICY_URL')
export const HYDRACHAIN_BLOG_URL = getEnvironmentVariable('REACT_APP_HYDRACHAIN_BLOG_URL')
export const HYDRACHAIN_DAO_URL = getEnvironmentVariable('REACT_APP_HYDRACHAIN_DAO_URL')

export enum NetworkType {
  L1,
  L2,
}
interface BaseChainInfo {
  readonly networkType: NetworkType
  readonly blockWaitMsBeforeWarning?: number
  readonly docs: string
  readonly bridge?: string
  readonly explorer: string
  readonly infoLink: string
  readonly logoUrl: string
  readonly circleLogoUrl?: string
  readonly label: string
  readonly helpCenterUrl?: string
  readonly nativeCurrency: {
    name: string // e.g. 'Goerli ETH',
    symbol: string // e.g. 'gorETH',
    decimals: number // e.g. 18,
  }
  readonly color?: string
  readonly backgroundColor?: string
}

interface L1ChainInfo extends BaseChainInfo {
  readonly networkType: NetworkType.L1
  readonly defaultListUrl?: string
  readonly label: string
}

export interface L2ChainInfo extends BaseChainInfo {
  readonly networkType: NetworkType.L2
  readonly bridge: string
  readonly statusPage?: string
  readonly defaultListUrl: string
  readonly label: string
}

type ChainInfoMap = { readonly [chainId: number]: L1ChainInfo | L2ChainInfo }

const CHAIN_INFO: ChainInfoMap = {
  // SAMVI Info: Chain info for each chain
  [SupportedChainId.HYDRA]: {
    networkType: NetworkType.L1,
    docs: HYDRACHAIN_DOCS_URL,
    explorer: MAINNET_EXPLORER,
    infoLink: HYDRACHAIN_INFO_URL,
    label: 'Hydra Chain',
    logoUrl: hydraLogoBlue,
    circleLogoUrl: polygonCircleLogoUrl,
    nativeCurrency: { name: 'Hydra', symbol: 'HYDRA', decimals: 18 },
    defaultListUrl: MAINNET_HYDRASWAP_LIST,
    color: darkTheme.chain_137,
    backgroundColor: darkTheme.chain_137_background,
  },
  [SupportedChainId.TESTNET]: {
    networkType: NetworkType.L1,
    docs: HYDRACHAIN_DOCS_URL,
    explorer: TESTNET_EXPLORER,
    infoLink: HYDRACHAIN_INFO_URL,
    label: 'Hydra Chain Testnet',
    logoUrl: hydraLogoWhite,
    circleLogoUrl: polygonCircleLogoUrl,
    nativeCurrency: { name: 'Hydra Testnet', symbol: 'tHYDRA', decimals: 18 },
    defaultListUrl: TESTNET_HYDRASWAP_LIST,
    color: darkTheme.chain_137,
    backgroundColor: darkTheme.chain_137_background,
  },
  [SupportedChainId.DEVNET]: {
    networkType: NetworkType.L1,
    docs: HYDRACHAIN_DOCS_URL,
    explorer: DEVNET_EXPLORER,
    infoLink: HYDRACHAIN_INFO_URL,
    label: 'Hydra Chain Devnet',
    logoUrl: hydraLogoBlack,
    circleLogoUrl: polygonCircleLogoUrl,
    nativeCurrency: { name: 'Hydra Devnet', symbol: 'dHYDRA', decimals: 18 },
    defaultListUrl: DEVNET_HYDRASWAP_LIST,
    color: darkTheme.chain_137,
    backgroundColor: darkTheme.chain_137_background,
  },
}

export function getChainInfo(chainId: SupportedL1ChainId): L1ChainInfo
export function getChainInfo(chainId: SupportedChainId): L1ChainInfo | L2ChainInfo
export function getChainInfo(
  chainId: SupportedChainId | SupportedL1ChainId | number | undefined
): L1ChainInfo | L2ChainInfo | undefined

/**
 * Overloaded method for returning ChainInfo given a chainID
 * Return type varies depending on input type:
 * number | undefined -> returns chaininfo | undefined
 * SupportedChainId -> returns L1ChainInfo | L2ChainInfo
 * SupportedL1ChainId -> returns L1ChainInfo
 */
export function getChainInfo(chainId: any): any {
  if (chainId) {
    return CHAIN_INFO[chainId] ?? undefined
  }
  return undefined
}

const HYDRA_INFO = CHAIN_INFO[SupportedChainId.HYDRA]
export function getChainInfoOrDefault(chainId: number | undefined) {
  return getChainInfo(chainId) ?? HYDRA_INFO
}
